import React, { FC, useCallback, useMemo, useReducer } from 'react';

import clsx from 'clsx';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardProps,
  FormControl,
  GridLegacy as Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import ImageUploadField from '~/components/Common/Forms/ImageUploadField';
import MarkdownEditor from '~/components/Common/Forms/widgets/MarkdownEditor';

import { allRegions } from '~/consts/allRegions';
import { brands } from '~/consts/brands';

import { toDayJs } from '~/services/TimeService';

import { validateCampaignLuxPlusPrice, validateCampaignPrice } from './campaignPricing';

const HeroCard = styled(Card)`
  &.error {
    border-color: red;
  }
` as FC<CardProps>;

const dealTypes: Array<{ id: string; label: string; value: string }> = [
  {
    id: '1',
    label: 'Airline',
    value: 'airline',
  },
  {
    id: '2',
    label: 'Destination',
    value: 'destination',
  },
  {
    id: '3',
    label: 'Departure Aiport',
    value: 'departure_airport',
  },
  {
    id: '4',
    label: 'All',
    value: 'all',
  },
];

const fareTypes = [
  { value: 'return', label: 'Return' },
  { value: 'oneWay', label: 'One Way' },
  { value: 'multiCity', label: 'Multi City' },
] as const;

const fareClasses = [
  { value: 'economy', label: 'Economy' },
  { value: 'business', label: 'Business' },
  { value: 'premium', label: 'Premium' },
  { value: 'first', label: 'First' },
] as const;

export interface FormState {
  formId: string;
  title: string;
  type: string;
  header: string;
  region: string;
  brand: Array<string>;
  startDate: string;
  endDate: string;
  terms: string;
  description: string;
  heroImage: string | undefined;
  formErrors: Partial<Record<FormKeys, string>>;
  airlineCode: string;
  price: string;
  luxPlusPrice: string;
  fareType: App.FareType;
  fareClass: App.FareClass;
}

export const initialState: FormState = {
  formId: uuid(),
  title: '',
  type: 'airline',
  header: '',
  region: 'AU',
  brand: ['luxuryescapes'],
  startDate: new Date(Date.now()).toISOString(),
  endDate: new Date(Date.now()).toISOString(),
  terms: '',
  description: '',
  heroImage: undefined,
  formErrors: {},
  airlineCode: '',
  price: '0',
  luxPlusPrice: '',
  fareType: 'return',
  fareClass: 'economy',
};

export enum FormKeys {
  TITLE = 'title',
  TYPE = 'type',
  HEADER = 'header',
  REGION = 'region',
  BRAND = 'brand',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  TERMS = 'terms',
  DESCRIPTION = 'description',
  HERO_IMAGE = 'heroImage',
  AIRLINE_CODE = 'airlineCode',
  PRICE = 'price',
  LUX_PLUS_PRICE = 'luxPlusPrice',
  FARE_TYPE = 'fareType',
  FARE_CLASS = 'fareClass',
}

export enum ActionTypes {
  CHANGE_VALUE = 'changeValue',
  SET_ERRORS = 'setErrors',
  CLEAR_ERRORS = 'clearErrors',
  SET_LOADING = 'setLoading',
  SET_GLOBAL_ERROR = 'setGlobalError',
  CLEAR_FORM_DATA = 'clearFormData',
}

type ChangeValueAction = {
  type: ActionTypes.CHANGE_VALUE;
  payload: {
    value: string | Array<string> | boolean;
    key: FormKeys;
  };
};

type SetErrorAction = {
  type: ActionTypes.SET_ERRORS;
  payload: Partial<Record<FormKeys, string>>;
};

type ClearErrorAction = {
  type: ActionTypes.CLEAR_ERRORS;
};

type ClearFormDataAction = {
  type: ActionTypes.CLEAR_FORM_DATA;
};

export type FormActions = ChangeValueAction | SetErrorAction | ClearErrorAction | ClearFormDataAction;

export function formReducer(state: FormState, action: FormActions) {
  switch (action.type) {
    case ActionTypes.CHANGE_VALUE: {
      const nextState = { ...state, [action.payload.key]: action.payload.value };
      const priceChanged = action.payload.key === FormKeys.PRICE || action.payload.key === FormKeys.LUX_PLUS_PRICE;
      return {
        ...state,
        [action.payload.key]: action.payload.value,
        formErrors: {
          ...state.formErrors,
          ...(action.payload.value !== state[action.payload.key] && { [action.payload.key]: undefined }),
          ...(priceChanged && {
            [FormKeys.PRICE]: validateCampaignPrice(nextState.price),
            [FormKeys.LUX_PLUS_PRICE]: validateCampaignLuxPlusPrice(nextState.price, nextState.luxPlusPrice),
          }),
        },
      };
    }

    case ActionTypes.SET_ERRORS:
      return {
        ...state,
        formErrors: action.payload,
      };

    case ActionTypes.CLEAR_ERRORS:
      return {
        ...state,
        formErrors: {},
      };

    case ActionTypes.CLEAR_FORM_DATA:
      return {
        ...initialState,
        formId: uuid(),
      };

    default:
      return state;
  }
}

function changeValue(key: FormKeys, value: string | Array<string> | boolean): ChangeValueAction {
  return {
    type: ActionTypes.CHANGE_VALUE,
    payload: {
      key,
      value,
    },
  };
}

function setErrors(errors: Partial<Record<FormKeys, string>>): SetErrorAction {
  return {
    type: ActionTypes.SET_ERRORS,
    payload: errors,
  };
}

function clearErrors(): ClearErrorAction {
  return {
    type: ActionTypes.CLEAR_ERRORS,
  };
}

function validateFormState(formState: FormState): {
  errors: Partial<Record<FormKeys, string>>;
  isValid: boolean;
} {
  const errors: Partial<Record<FormKeys, string>> = {};
  let isValid = true;

  if (!formState.description.trim()) {
    isValid = false;
    errors[FormKeys.DESCRIPTION] = 'Description is required';
  }

  if (!formState.terms.trim()) {
    isValid = false;
    errors[FormKeys.TERMS] = 'Terms & Conditions are required';
  }

  if (!formState.heroImage) {
    isValid = false;
    errors[FormKeys.HERO_IMAGE] = 'Hero image is required';
  }

  const standardPriceError = validateCampaignPrice(formState.price);
  if (standardPriceError) {
    isValid = false;
    errors[FormKeys.PRICE] = standardPriceError;
  }

  const priceError = validateCampaignLuxPlusPrice(formState.price, formState.luxPlusPrice);
  if (priceError) {
    isValid = false;
    errors[FormKeys.LUX_PLUS_PRICE] = priceError;
  }

  return {
    isValid,
    errors,
  };
}

export type OnSaveFormData = Omit<FormState, 'formErrors' | 'formId'>;

interface Props {
  onSubmit: (data: FormState) => void;
  loading?: boolean;
  error?: string;
  initialValues?: Partial<OnSaveFormData>;
  submitLabel?: string;
}

export function FlightsCampaignsForm({ onSubmit, loading, error, initialValues, submitLabel = 'Submit' }: Props) {
  const [formState, dispatch] = useReducer(formReducer, initialState, () => ({ ...initialState, ...initialValues }));

  const onDateChange = (key: FormKeys, value: { valueOf(): number } | null) => {
    const time = value ? value.valueOf() : NaN;
    if (Number.isFinite(time)) dispatch(changeValue(key, new Date(time).toISOString()));
  };

  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      dispatch(clearErrors());

      const { isValid, errors } = validateFormState(formState);

      if (!isValid) {
        dispatch(setErrors(errors));
        return;
      }

      onSubmit({
        ...formState,
        fareClass: formState.fareClass || null,
        fareType: formState.fareType || null,
      });
    },
    [formState, onSubmit],
  );

  const startDate = useMemo(() => {
    return toDayJs(formState.startDate);
  }, [formState.startDate]);

  const endDate = useMemo(() => {
    return toDayJs(formState.endDate);
  }, [formState.endDate]);

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={4} alignItems="end">
          <Grid item xs={4}>
            <FormControl fullWidth>
              <TextField
                id="title"
                slotProps={{ inputLabel: { shrink: true } }}
                label="Title"
                variant="outlined"
                value={formState.title}
                onChange={(event) => dispatch(changeValue(FormKeys.TITLE, event.target.value))}
                required
                fullWidth
              />
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth required>
              <InputLabel id="type-label" shrink>
                Type
              </InputLabel>

              <Select
                labelId="type-label"
                notched
                id="type"
                value={formState.type}
                label="Type"
                required
                onChange={(event) => dispatch(changeValue(FormKeys.TYPE, event.target.value))}
              >
                {dealTypes.map((deal) => (
                  <MenuItem key={deal.id} value={deal.value}>
                    {deal.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth>
              <TextField
                id="header"
                slotProps={{ inputLabel: { shrink: true } }}
                label="Header"
                variant="outlined"
                value={formState.header}
                onChange={(event) => dispatch(changeValue(FormKeys.HEADER, event.target.value))}
                required
                fullWidth
              />
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth required>
              <InputLabel id="region-label" shrink>
                Region
              </InputLabel>

              <Select
                labelId="region-label"
                notched
                id="region"
                value={formState.region}
                label="Region"
                onChange={(event) => dispatch(changeValue(FormKeys.REGION, event.target.value))}
                required
              >
                {allRegions.map((region) => (
                  <MenuItem key={region.code} value={region.code}>
                    {region.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth>
              <DateTimePicker
                label="Start date"
                slotProps={{ textField: { required: true, slotProps: { inputLabel: { shrink: true } } } }}
                value={startDate}
                onChange={(value) => onDateChange(FormKeys.START_DATE, value)}
              />
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth>
              <DateTimePicker
                label="End date"
                slotProps={{ textField: { required: true, slotProps: { inputLabel: { shrink: true } } } }}
                value={endDate}
                onChange={(value) => onDateChange(FormKeys.END_DATE, value)}
              />
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth required>
              <InputLabel id="brand-label" shrink>
                Brand
              </InputLabel>

              <Select
                labelId="brand-label"
                notched
                id="brand"
                multiple
                value={formState.brand}
                onChange={(event) => dispatch(changeValue(FormKeys.BRAND, event.target.value))}
                input={<OutlinedInput label="Brand" />}
                required
              >
                {brands.map((brand) => (
                  <MenuItem key={brand.value} value={brand.value}>
                    {brand.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth>
              <TextField
                id="price"
                required
                slotProps={{ inputLabel: { shrink: true } }}
                label="Price"
                variant="outlined"
                type="number"
                value={formState.price}
                error={!!formState.formErrors.price}
                helperText={formState.formErrors.price}
                onChange={(event) => dispatch(changeValue(FormKeys.PRICE, event.target.value))}
                InputProps={{ inputProps: { step: '0.01' } }}
                fullWidth
              />
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <TextField
              id="luxPlusPrice"
              slotProps={{ inputLabel: { shrink: true } }}
              label="LuxPlus price"
              type="number"
              value={formState.luxPlusPrice}
              onChange={(event) => dispatch(changeValue(FormKeys.LUX_PLUS_PRICE, event.target.value))}
              error={!!formState.formErrors.luxPlusPrice}
              helperText={formState.formErrors.luxPlusPrice}
              InputProps={{ inputProps: { min: '0.01', max: '9999999999.99', step: '0.01' } }}
              fullWidth
            />
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth>
              <TextField
                id="airlineCode"
                slotProps={{ inputLabel: { shrink: true } }}
                label="Airline code"
                variant="outlined"
                value={formState.airlineCode}
                onChange={(event) => dispatch(changeValue(FormKeys.AIRLINE_CODE, event.target.value))}
                InputProps={{ inputProps: { maxlength: 2 } }}
                fullWidth
              />
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel id="fareType-label" shrink>
                Fare Type
              </InputLabel>

              <Select
                labelId="fareType-label"
                notched
                id="fareType"
                value={formState.fareType}
                label="Fare Type"
                onChange={(event) => dispatch(changeValue(FormKeys.FARE_TYPE, event.target.value))}
              >
                <MenuItem value="">
                  <Typography fontStyle="italic">None</Typography>
                </MenuItem>

                {fareTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={4}>
            <FormControl fullWidth>
              <InputLabel id="fareClass-label" shrink>
                Fare Class
              </InputLabel>

              <Select
                labelId="fareClass-label"
                notched
                id="fareClass"
                value={formState.fareClass}
                label="Fare Class"
                onChange={(event) => dispatch(changeValue(FormKeys.FARE_CLASS, event.target.value))}
              >
                <MenuItem value="">
                  <Typography fontStyle="italic">None</Typography>
                </MenuItem>

                {fareClasses.map((fareClass) => (
                  <MenuItem key={fareClass.value} value={fareClass.value}>
                    {fareClass.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Stack spacing={2}>
              <Typography fontWeight={500}>Hero Image *</Typography>

              <HeroCard
                variant="outlined"
                className={clsx({
                  error: formState.formErrors.heroImage,
                })}
              >
                <CardContent>
                  <ImageUploadField
                    label="Hero Image"
                    value={formState.heroImage}
                    multiple={false}
                    onUpload={(_, newValue) => dispatch(changeValue(FormKeys.HERO_IMAGE, newValue))}
                  />
                </CardContent>
              </HeroCard>

              {formState.formErrors.heroImage && <Typography color="red">{formState.formErrors.heroImage}</Typography>}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack spacing={2}>
              <Typography fontWeight={500}>Description *</Typography>

              <MarkdownEditor
                key={`${formState.formId}-desc`}
                value={formState.description}
                onChange={(value) => dispatch(changeValue(FormKeys.DESCRIPTION, value))}
                error={formState.formErrors.description}
              />

              {formState.formErrors.description && (
                <Typography color="red">{formState.formErrors.description}</Typography>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Stack spacing={2}>
              <Typography fontWeight={500}>Terms & Conditions *</Typography>

              <MarkdownEditor
                key={`${formState.formId}-terms`}
                value={formState.terms}
                onChange={(value) => dispatch(changeValue(FormKeys.TERMS, value))}
                error={formState.formErrors.terms}
              />

              {formState.formErrors.terms && <Typography color="red">{formState.formErrors.terms}</Typography>}
            </Stack>
          </Grid>

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? 'Loading...' : submitLabel}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}
